import { Chip } from "@mui/material";
import { ChipWrap } from "components/ChipWrap";
import { Box } from "@mui/system";
import { useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { AddNewAlert } from "./AddAlertPolicy";
import { FormDrawer } from "components/FormDrawer";
import { ListPageHeader } from "components/ListPageHeader";
import { ConfirmDialog } from "components/ConfirmDialog";
import DataTable, { Column } from "components/Table";
import { useResourceList } from "hooks/useResourceList";
import { useDeleteDialog } from "hooks/useDeleteDialog";
import { useFormDrawer } from "hooks/useFormDrawer";
import { ChipList } from "components/ChipList";
import { Circle, ToggleOffOutlined, ToggleOnOutlined } from "@mui/icons-material";
import { formatDateTime, fromUnix } from "helpers/dateFormat";
import { useRole } from "hooks/useRole";

interface AlertListStateItem {
    id: string
    name: string
    description: string
    enable: boolean
    notificationTypes: number[]
    emailRecipients: string[]
    slackChannels: string[]
    lastTimeTriggered: number
    nextTimeTrigger: number
    createdat: Date
    updatedat: Date
}

type columnsType = "name" | "description" | "lastTimeTriggered" | "nextTimeTrigger" | "emailRecipients" | "enable" | "createdat" | "updatedat";

const defaultColumns: Column<columnsType>[] = [
    { columnId: "name", width: "15%", direction: "desc", sortOrder: 1 },
    { columnId: "description", width: "15%" },
    { columnId: "lastTimeTriggered", width: "10%" },
    { columnId: "nextTimeTrigger", width: "10%" },
    { columnId: "emailRecipients", width: "20%" },
    { columnId: "enable", width: "10%" },
    { columnId: "createdat", width: "10%", direction: "desc", sortOrder: 2 },
    { columnId: "updatedat", width: "10%" },
];

function toAlert(x: any): AlertListStateItem {
    return {
        id: x.id,
        name: x.name,
        enable: x.enable,
        description: x.description,
        notificationTypes: x.notificationTypes || [],
        emailRecipients: x.emailRecipients || [],
        slackChannels: x.slackChannels || [],
        lastTimeTriggered: x.lastTimeTriggered,
        nextTimeTrigger: x.nextTimeTrigger,
        createdat: fromUnix(x.createdAt),
        updatedat: fromUnix(x.updatedAt),
    };
}

export default function Alerts() {
    const notification = useNotification();
    const login = useLogin();
    const { canEditPolicy } = useRole();
    //alerts are fetched in full: the list is short and the page has never paged
    const list = useResourceList<AlertListStateItem, columnsType>({
        url: "/api/alerts",
        columnsKey: "dataGrid_AlertTable_columns_v2",
        defaultColumns,
        mapItem: toAlert,
        sorted: true,
        searchable: true,
    });

    const setAlertEnabled = async (id: string, state: boolean) => {
        const req = login.GetAxios();

        if (!req) {
            return; //not logged in, nothing to do
        }

        try {
            await req.put(`/api/alerts/${id}/enable`, {
                enable: state,
            });
            list.reload();
            notification.Display({
                type: "success",
                title: "Success",
                message: "Alert status updated successfully"
            });
        } catch (err) {
            notification.Display({
                title: "Error",
                message: "Could not update rule status",
                type: "error",
            });
        }
    };

    const drawer = useFormDrawer();
    const del = useDeleteDialog({
        url: "/api/alerts",
        label: "alert",
        onDeleted: list.reload,
    });

    return <>
        <Box display="flex" flexDirection="column">
            <ListPageHeader
                title="Alerts"
                search={list.search}
                onSearchChange={list.setSearch}
                searchAllFields={list.searchAllFields}
                onSearchAllFieldsChange={list.setSearchAllFields}
                actionId="new-alert-button"
                actionLabel="Add New Alert"
                // NS-753
                onAction={canEditPolicy ? drawer.add : undefined}
            />

            <DataTable
                {...list.tableProps}
                columns={{
                    name: {
                        displayName: "Name",
                        sortable: true,
                        render: (x) => x.name,
                    },
                    description: {
                        displayName: "Description",
                        sortable: true,
                        render: (x) => x.description,
                    },
                    lastTimeTriggered: {
                        displayName: "Last Time Triggered",
                        sortable: true,
                        render: (x) => {
                            if (x.lastTimeTriggered) {
                                return formatDateTime(fromUnix(x.lastTimeTriggered));
                            }

                            return "Never";
                        }
                    },
                    nextTimeTrigger: {
                        displayName: "Next Check",
                        sortable: true,
                        render: (x) => {
                            if (x.nextTimeTrigger && x.enable) {
                                return formatDateTime(fromUnix(x.nextTimeTrigger));
                            }

                            return "None";
                        }
                    },
                    emailRecipients: {
                        displayName: "Notifications",
                        render: (x) => {
                            return <ChipWrap>
                                <ChipList items={x.emailRecipients} renderItem={(x) => <Chip label={x} title={x} variant="outlined" size="small" color="primary" />} />
                                <ChipList items={x.slackChannels} renderItem={(x) => <Chip label={x} title={x} variant="outlined" size="small" color="success" />} />
                                {x.notificationTypes && x.notificationTypes.includes(3) && <Chip label={"Webhook"} title={"Webhook"} variant="outlined" size="small" color="warning" /> }
                                {x.notificationTypes && x.notificationTypes.includes(4) && <Chip label={"Teams"} title={"Teams"} variant="outlined" size="small" color="secondary" /> }
                            </ChipWrap>;

                        },
                    },
                    enable: {
                        displayName: "Status",
                        render: (x) => {
                            return x.enable
                                ? <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                }}>
                                    <Circle sx={{transform: "scale(0.6)"}} fontSize="small" color="success" /><span>Active</span>
                                </Box>
                                : <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                }}>
                                    <Circle sx={{transform: "scale(0.6)"}} fontSize="small" color="error" /><span>Inactive</span>
                                </Box>;
                        }
                    },
                    createdat: {
                        displayName: "Created At",
                        sortable: true,
                        render: (x) => {
                            return formatDateTime(x.createdat);
                        }
                    },
                    updatedat: {
                        displayName: "Updated At",
                        sortable: true,
                        render: (x) => {
                            return formatDateTime(x.updatedat);
                        }
                    },
                }}
                rowOptionsMenu={[
                    {
                        icon: <EditIcon className="edit-button" fontSize="small" />,
                        text: canEditPolicy ? "Edit" : "View",
                        onClick: (x) => {
                            drawer.edit(x.id);
                        }
                    },
                    {
                        isVisible: () => canEditPolicy,
                        icon: (x) => {
                            if(x.enable) {
                                return <ToggleOffOutlined/>;
                            }
                            return <ToggleOnOutlined/>;
                        },
                        text: (x) => {
                            if(x.enable) {
                                return "Turn Off";
                            } 
                            return "Turn On";
                        },
                        onClick: (x) => {
                            setAlertEnabled(x.id, !x.enable);
                        }
                    },
                    {
                        icon: <DeleteIcon className="delete-button" fontSize="small" />,
                        text: "Delete",
                        isVisible: () => canEditPolicy,
                        onClick: (x) => {
                            del.ask(x.name, x.id);
                        }
                    },
                ]}
            />
        </Box>

        <ConfirmDialog {...del.dialogProps} title="Delete Alert" />

        <FormDrawer {...drawer.drawerProps}>
            <AddNewAlert
                alertId={drawer.editingId}
                onExit={drawer.close}
                onSave={() => { list.reload(); drawer.close(); }}
            />
        </FormDrawer>
    </>;
}


